import { BRIDGE_METHODS } from '@/common/bridge/constants'
import { bridge } from '@/common/bridge/methods'
import { isInHostMiniAppWebView, isInWeChatMiniProgram } from '@/utils/tool'
import { ascfWebNavigateTo } from '@/utils/ascfWeb'
import { router } from '@/router'
import { appendSourceFlagToUrl } from '@h5-journey/utils'
import wx from 'weixin-js-sdk'

const HOST_WEBVIEW_LOGIN_KEYS = [
  'loginToken',
  'memberId',
  'loginId',
  'mobile',
  'code',
  'client',
  'sourceFlag',
]

/**
 * 给目标 URL 补上当前会话登录参数（不覆盖已有 query）
 * 新开的宿主 webview 不共享 sessionStorage，老 client 需要从 URL 取登录态
 * @param {string} url
 * @returns {string}
 */
export const appendHostWebViewLoginParams = url => {
  if (!url) return url
  const extra = {}
  HOST_WEBVIEW_LOGIN_KEYS.forEach(key => {
    const value = sessionStorage.getItem(key)
    if (value) extra[key] = value
  })
  if (!extra.client) extra.client = 'wxapp'

  const hashIndex = url.indexOf('#')
  // hashIndex === 0 表示本身已是 #/path?...，不能再按 hash 切开，否则会无限递归
  if (hashIndex > 0) {
    return url.slice(0, hashIndex) + appendHostWebViewLoginParams(url.slice(hashIndex))
  }

  const [base, search = ''] = url.split('?')
  const searchParams = new URLSearchParams(search)
  Object.entries(extra).forEach(([key, value]) => {
    if (!searchParams.has(key)) searchParams.set(key, value)
  })
  const query = searchParams.toString()
  return query ? `${base}?${query}` : base
}

/**
 * 同应用内 SPA 跳转（鸿蒙元服务无可用 wx.miniProgram 时使用）
 * @param {string} url 以 / 开头的站内路径，可带 query
 */
export const navigateInCurrentApp = url => {
  if (!url || !url.startsWith('/')) return false
  const [path, search] = url.split('?')
  const query = {}
  if (search) {
    new URLSearchParams(search).forEach((value, key) => {
      query[key] = value
    })
  }
  router.push({ path, query })
  return true
}

/**
 * 路由前缀
 * @type {Object}
 * @property {string} client 景点门票路由前缀
 * @property {string} newJourney 当前项目路由前缀
 */
const routePrefix = {
  client: '/#/commonModule/pages',
  newJourney: '/new-journey',
}

/**
 * 跳转flutter webview页面，支持push和go
 * @param {string} url 跳转的url，无需携带域名，如需参数，需要拼接在路由之后
 * @param {string} projectName 项目名称，client或newJourney
 * @param {string} type 跳转类型，push或go
 * @param {string} title 页面标题 必传，不传会丢失导航头
 * @param {string} [fullUrl] 完整的url，如果传递此参数，将忽略url和projectName参数，直接使用此完整url
 * @param {string} [cb] then callback
 * @returns {void} 无返回值
 */
export const NavigateToWebview = ({
  url,
  projectName = 'client',
  type = 'push',
  title,
  fullUrl,
  cb,
  hasHistory = true,
}) => {
  /**
   * 如果传递了完整URL，直接使用fullUrl，否则拼接routePrefix和url
   */
  let targetUrl
  if (fullUrl) {
    targetUrl = fullUrl
  } else {
    targetUrl = window.location.origin + routePrefix[projectName] + url
  }
  // 自动携带 sourceFlag
  targetUrl = appendSourceFlagToUrl(targetUrl)

  if (isInHostMiniAppWebView()) {
    // 微信小程序：新开 webview，由原生导航栏展示 title
    if (isInWeChatMiniProgram()) {
      const miniProgramUrl = `/pages/webview/index?url=${encodeURIComponent(targetUrl)}`
      try {
        wx?.miniProgram?.[hasHistory ? 'navigateTo' : 'redirectTo']({ url: miniProgramUrl })
      } catch (error) {
        console.error('小程序跳转失败:', error)
      }
      return
    }
    // 鸿蒙元服务：wx.miniProgram 通常不可用，调用会静默失败，表现为点了没反应
    if (projectName === 'newJourney' && !fullUrl && navigateInCurrentApp(url)) {
      return
    }
    // 老 client / 完整 URL：对齐微信新开宿主 webview，打开同一套「订单详情」；新 webview 不共享 sessionStorage，需带登录参数
    const webviewTarget = appendHostWebViewLoginParams(targetUrl)
    const miniProgramUrl = `/pages/webview/index?url=${encodeURIComponent(webviewTarget)}`
    if (ascfWebNavigateTo(miniProgramUrl, { replace: !hasHistory })) {
      return
    }
    window.location.href = webviewTarget
    return
  }

  // H5 浏览器环境：无 Flutter bridge，直接跳转
  if (!window.flutter_inappwebview) {
    if (hasHistory) {
      window.location.href = targetUrl
    } else {
      window.location.replace(targetUrl)
    }
    cb && cb()
    return
  }

  let name = `/web?url=${encodeURIComponent(targetUrl)}`
  if (title) {
    name += `&title=${title}`
  }
  if (hasHistory) {
    // hasHistory为true时，只执行OPEN_PAGE
    bridge
      .invokeSync(BRIDGE_METHODS.OPEN_PAGE, { type, name })
      .then(() => {
        console.log('跳转成功')
        cb && cb()
      })
      .catch(error => {
        console.error('跳转失败:', error)
      })
  } else {
    // hasHistory为false时，先执行ROUTER_POP，再执行OPEN_PAGE
    bridge
      .invokeSync(BRIDGE_METHODS.ROUTER_POP)
      .then(() => {
        return bridge.invokeSync(BRIDGE_METHODS.OPEN_PAGE, { type, name })
      })
      .then(() => {
        console.log('跳转成功')
        cb && cb()
      })
      .catch(error => {
        console.error('跳转失败:', error)
      })
  }
}
